/*
import {createContext, useEffect, useState} from "react";

export const BarCodeScannerContext = createContext({
    scannedString: '',
    setScannedString: (v) => {
    },
});

export function BarCodeScannerProvider({children}) {
    const [scannedString, setScannedString] = useState('')
    const [focus, setFocus] = useState(false)
    const [enableScan, setEnableScan] = useState(true)

    let scannedCode = ''
    let scanTimeout

    useEffect(() => {
        document.addEventListener('focus', (e) => {
            if (e.target.tagName === "INPUT") {
                setFocus(true)
            }
        }, true)

        document.addEventListener('blur', (e) => {
            if (e.target.tagName === "INPUT") {
                setFocus(false)
            }
        }, true)

        const keydownHandler = (event) => {
            clearTimeout(scanTimeout);

            if (!focus && enableScan) {
                if (event.key === "Enter") {
                    event.preventDefault();
                    setScannedString("");
                    setTimeout(() => {
                        setScannedString(scannedCode);
                        scannedCode = "";
                    }, 50);
                } else if (event.key !== "Shift") {
                    let key = event.key;

                    if (key === "Unidentified") {
                        // fallback : utiliser keyCode si c’est un chiffre/lettre
                        if (event.keyCode >= 32 && event.keyCode <= 126) {
                            key = String.fromCharCode(event.keyCode);
                        } else {
                            key = ""; // sinon on ignore
                        }
                    }

                    scannedCode += key;
                }
            }

            // Timeout → fin du scan
            scanTimeout = setTimeout(() => {
                scannedCode = "";
            }, 200);
        };

        document.addEventListener("keydown", keydownHandler);

        return () => {
            document.removeEventListener("keydown", keydownHandler);
        };

    }, []);

    function resetScannedString() {
        setScannedString('')
    }

    return (
        <BarCodeScannerContext.Provider value={{
            scannedString,
            setScannedString,
            resetScannedString,
            setEnableScan
        }}>
            {children}
        </BarCodeScannerContext.Provider>
    )
}*/

import { createContext, useEffect, useRef, useState } from "react";

export const BarCodeScannerContext = createContext({
    scannedString: "",
    setScannedString: () => {},
    resetScannedString: () => {},
    setEnableScan: () => {},
    enableScan: true,
    withScanDisabled: async (fn) => fn(),
});

export function BarCodeScannerProvider({ children }) {
    const [scannedString, setScannedString] = useState("");

    // Conservez un state visible pour l’UI si besoin
    const [enableScanState, setEnableScanState] = useState(true);
    const enableScanRef = useRef(true); // => utilisé par le handler (synchrone)

    // Focus sur un input: utilisons aussi une ref pour le handler
    const [focusState, setFocusState] = useState(false);
    const focusRef = useRef(false);

    // Variables internes au handler
    const scannedCodeRef = useRef("");
    const scanTimeoutRef = useRef(null);


    // Setter synchrone: met à jour la ref immédiatement
    const setEnableScan = (valueOrUpdater) => {
        const next =
            typeof valueOrUpdater === "function"
                ? valueOrUpdater(enableScanRef.current)
                : valueOrUpdater;
        enableScanRef.current = !!next; // visibilité immédiate pour le handler
        setEnableScanState(!!next); // éventuellement refléter côté UI
    };

    // Helper pratique pour désactiver temporairement le scan autour d’une action
    const withScanDisabled = async (fn) => {
        const prev = enableScanRef.current;
        try {
            setEnableScan(false);
            return await fn();
        } finally {
            setEnableScan(prev);
        }
    };

    const isEditableElement = (el) => {
        if (!el || el === document.body) return false;
        const tag = el.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
        if (el.isContentEditable) return true;
        const role = el.getAttribute && el.getAttribute("role");
        if (role === "textbox" || role === "combobox" || role === "searchbox") return true;
        // certains inputs custom MUI ont un input interne; on teste l'input descendant
        if (el.querySelector && (el.querySelector("input[contenteditable='true'], textarea[contenteditable='true']"))) return true;
        return false;
    };

    useEffect(() => {
        const updateFocusFromActive = () => {
            const active = document.activeElement;
            const hasFocus = isEditableElement(active);
            focusRef.current = hasFocus;
            setFocusState(hasFocus);
        };

        const onFocus = (e) => {
            if (e.target.tagName === "INPUT") {
                focusRef.current = true; // synchro pour le handler
                setFocusState(true); // si UI a besoin de savoir
            }
        };
        const onBlur = (e) => {
            if (e.target.tagName === "INPUT") {
                focusRef.current = false;
                setFocusState(false);
            }
        };

        const keydownHandler = (event) => {
            // Nettoyage du timeout éventuel
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }

            // Utiliser les refs pour un comportement synchrone
            if (!focusRef.current && enableScanRef.current) {
                if (event.key === "Enter") {
                    event.preventDefault();
                    // Émettez la valeur finale scannée
                    const value = scannedCodeRef.current;
                    scannedCodeRef.current = "";
                    // Optionnel: petit délai si vous devez signaler un changement distinct
                    setScannedString("");
                    setTimeout(() => setScannedString(value), 0);
                } else if (event.key !== "Shift") {
                    let key = ""
                    if(event.key === "§") {
                        key = "-"
                    } else {
                        key = event.key;
                    }

                    if (key === "Unidentified") {
                        if (event.keyCode >= 32 && event.keyCode <= 126) {
                            key = String.fromCharCode(event.keyCode);
                        } else {
                            key = ""; // ignorer
                        }
                    }

                    scannedCodeRef.current += key;
                }
            }

            // Timeout de fin de scan
            scanTimeoutRef.current = setTimeout(() => {
                scannedCodeRef.current = "";
            }, 200);
        };

        // document.addEventListener("focus", onFocus, true);
        // document.addEventListener("blur", onBlur, true);
        document.addEventListener('focusin', updateFocusFromActive)
        document.addEventListener('focusout', () => setTimeout(updateFocusFromActive, 0))
        document.addEventListener("keydown", keydownHandler);

        return () => {
            document.removeEventListener("focus", onFocus, true);
            document.removeEventListener("blur", onBlur, true);
            document.removeEventListener("keydown", keydownHandler);
        };
    }, []);

    function resetScannedString() {
        setScannedString("");
    }

    return (
        <BarCodeScannerContext.Provider
            value={{
                scannedString,
                setScannedString,
                resetScannedString,
                setEnableScan, // synchrone pour le handler via ref
                enableScan: enableScanState,
                withScanDisabled,
            }}
        >
            {children}
        </BarCodeScannerContext.Provider>
    );
}
